import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import {
  Container,
  Actions,
  TokenDisplay,
  TokenActions,
  SelectContainer,
} from './styles';
import { Button } from '../../../components/Button';
import api from '../../../services/api';

interface IntegrationSystemDTO {
  id: number;
  name: string;
  isActive: boolean;
}

interface ProfessionalTokenPayload {
  token: string;
  officeIds: number[];
  practiceIds: number[];
  businessUnitIds: number[];
  comanagementIds: number[];
  scopes: string[];
}

interface DialogCustomProps {
  professionalId: number;
  isOpen: boolean;
  handleClose(): void;
}

export const GenerateTokenDialog: React.FC<DialogCustomProps> = ({
  professionalId,
  isOpen,
  handleClose,
}) => {
  const [payload, setPayload] = useState<ProfessionalTokenPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [systems, setSystems] = useState<IntegrationSystemDTO[]>([]);
  const [selectedSystem, setSelectedSystem] =
    useState<IntegrationSystemDTO | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoadError(null);
      try {
        const systemsResponse = await api.get('/api/integration-systems');
        setSystems(
          systemsResponse.data.filter((s: IntegrationSystemDTO) => s.isActive),
        );
      } catch (caughtError) {
        const err = caughtError as {
          response?: { data?: { message?: string } };
        };
        setLoadError(err?.response?.data?.message || 'Error loading systems');
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const handleGenerateToken = useCallback(async () => {
    setLoading(true);
    setCopied(false);
    setError(null);
    try {
      const response = await api.post(
        `api/token/generate/${professionalId}`,
        selectedSystem,
      );
      setPayload(response.data);
    } catch (err) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError?.response?.data?.message ||
          'Error generating token. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [professionalId, selectedSystem]);

  const handleCopyToken = useCallback(() => {
    if (payload?.token) {
      navigator.clipboard.writeText(`Bearer ${payload.token}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [payload]);

  const handleCloseDialog = useCallback(() => {
    setPayload(null);
    setError(null);
    setCopied(false);
    setSelectedSystem(null);
    setLoadError(null);
    handleClose();
  }, [handleClose]);

  return (
    <Container anchor="right" open={isOpen} onClose={handleCloseDialog}>
      <Box width={600}>
        <Typography
          variant="h5"
          component="h5"
          style={{
            fontWeight: 500,
            marginTop: '15px',
            marginBottom: '15px',
            marginLeft: '16px',
          }}
        >
          Generate JWT Token
        </Typography>

        <Typography
          variant="body2"
          style={{
            marginLeft: '16px',
            marginRight: '16px',
            color: '#666',
          }}
        >
          Generate a JWT token for testing purposes for professional ID:{' '}
          <strong>{professionalId}</strong>
        </Typography>

        {loadError && (
          <Typography
            variant="body2"
            style={{
              marginLeft: '16px',
              marginRight: '16px',
              marginTop: '16px',
              color: '#d32f2f',
            }}
          >
            {loadError}
          </Typography>
        )}

        <SelectContainer>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="system-select-label">System</InputLabel>
            <Select
              labelId="system-select-label"
              value={selectedSystem?.id || ''}
              onChange={e => {
                const system = systems.find(s => s.id === e.target.value);
                setSelectedSystem(system || null);
              }}
              label="System"
            >
              {systems.map(system => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </SelectContainer>

        <TokenActions>
          <Button primary onClick={handleGenerateToken} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Token'}
          </Button>
          {payload && (
            <Button onClick={handleCopyToken}>
              {copied ? 'Copied!' : 'Copy Token'}
            </Button>
          )}
        </TokenActions>

        {error && (
          <Typography
            variant="body2"
            style={{
              marginLeft: '16px',
              marginRight: '16px',
              marginTop: '16px',
              color: '#d32f2f',
            }}
          >
            {error}
          </Typography>
        )}

        {payload && (
          <>
            <TokenDisplay>Bearer {payload.token}</TokenDisplay>

            <Box
              style={{
                margin: '16px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {`practiceIds: [${payload.practiceIds.join(', ')}]
officeIds: [${payload.officeIds.join(', ')}]
businessUnitIds: [${payload.businessUnitIds.join(', ')}]
comanagementIds: [${payload.comanagementIds.join(', ')}]
scopes: [${payload.scopes.map(s => `"${s}"`).join(', ')}]`}
            </Box>
          </>
        )}

        <Actions>
          <Button
            onClick={handleCloseDialog}
            style={{ marginRight: '16px', marginBottom: '16px' }}
          >
            Close
          </Button>
        </Actions>
      </Box>
    </Container>
  );
};
